import React, { useState, useCallback } from "react";
import { CalculatorSliderField } from "@/components/design-system/Calculator/CalculatorSliderField";
import { CalculatorStatCard } from "@/components/design-system/Calculator/CalculatorStatCard";
import { CalculatorBarChart } from "@/components/design-system/Calculator/CalculatorBarChart";
import { CalculatorFooter } from "@/components/design-system/Calculator/CalculatorFooter";
import styles from "@/components/design-system/Calculator/Calculator.module.css";
import {
  type CreditUnionCalculatorInputs,
  type CreditUnionCalculatorResults,
  DEFAULT_INPUTS,
  formatCompactDollar,
  formatCount,
  calcResults,
} from "./creditUnionCalculator.utils";

export const CreditUnionCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<CreditUnionCalculatorInputs>(DEFAULT_INPUTS);
  const results: CreditUnionCalculatorResults = calcResults(inputs);

  const handleChange = useCallback((field: keyof CreditUnionCalculatorInputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div
      className="bg-primary-500 rounded-7 relative mx-auto w-full max-w-235 overflow-hidden font-sans"
      style={{ boxShadow: "0 4px 40px 0 #361637, 0 32px 80px rgba(45,18,48,0.45), 0 8px 24px rgba(0,0,0,0.15)" }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 75% 60% at -5% 105%, rgba(255,255,255,0.12) 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 105% -5%, rgba(45,18,48,0.6) 0%, transparent 55%)" }}
      />

      <div className="relative z-1 p-[28px_28px_22px]">
        <div className="mb-5.5 flex flex-wrap items-center justify-between gap-3">
          <img src="/logo.png" alt="Bravo" className="h-8 w-auto brightness-0 invert" />
          <div className="text-[11px] flex items-center gap-1.75 rounded-full border border-white/30 bg-white/10 px-3.5 py-1.25 font-bold tracking-[0.07em] text-white uppercase">
            <span className={`h-1.5 w-1.5 shrink-0 rounded-full bg-white ${styles.badgeDot}`} />
            Bravo Member Engagement Calculator
          </div>
        </div>

        <p className="mx-auto mb-4.5 max-w-125 text-center text-sm leading-normal text-white/75">
          How much value is your financial wellness program leaving on the table? Enter your membership data and see what genuinely engaged members could mean for your credit union.
        </p>

        <div className={styles.calcGrid}>
          {/* FORM PANEL */}
          <div className="rounded-[18px] border border-white/60 bg-[#faf6fb] p-[22px_20px_24px]">
            <div className="text-primary-500 text-[11px] mb-4 text-center font-extrabold tracking-[0.08em] uppercase">
              Your Membership
            </div>
            <div className={`${styles.sectionHeading} ${styles.sectionHeadingFirst}`}>Core Inputs</div>

            <CalculatorSliderField label="Total membership" tooltip="Total number of members in your credit union." value={inputs.totalMembers} min={5000} max={200000} step={1000} minLabel="5,000" maxLabel="200,000" onChange={(val) => handleChange("totalMembers", val)} />
            <CalculatorSliderField label="Current wellness engagement rate" tooltip="Industry average is ~8–12% for traditional financial wellness programs." value={inputs.currentRate} min={2} max={30} step={1} suffix="%" minLabel="2%" maxLabel="30%" onChange={(val) => handleChange("currentRate", val)} />
            <CalculatorSliderField label="Target engagement rate with Bravo" tooltip="AI chat typically drives 2–7x higher engagement than static portals." value={inputs.targetRate} min={15} max={60} step={1} suffix="%" minLabel="15%" maxLabel="60%" onChange={(val) => handleChange("targetRate", val)} />
            <CalculatorSliderField label="Avg. products per engaged member" tooltip="Typical range is 2.0–3.0 products per engaged member." value={inputs.avgProducts} min={1} max={5} step={0.1} minLabel="1.0" maxLabel="5.0" onChange={(val) => handleChange("avgProducts", val)} />
            <CalculatorSliderField label="Average loan balance per member" tooltip="Used for loan origination revenue modeling." value={inputs.avgLoan} min={2000} max={50000} step={1000} prefix="$" minLabel="$2K" maxLabel="$50K" onChange={(val) => handleChange("avgLoan", val)} />
            <CalculatorSliderField label="Average deposit balance per member" tooltip="Average across savings and checking accounts." value={inputs.avgDeposit} min={1000} max={30000} step={500} prefix="$" minLabel="$1K" maxLabel="$30K" onChange={(val) => handleChange("avgDeposit", val)} />

            <button
              className="rounded-[10px] mt-4 block w-full cursor-pointer border-none p-3 text-sm font-extrabold text-white transition-[opacity,transform] duration-200 hover:-translate-y-px hover:opacity-88"
              style={{ background: "linear-gradient(37.77deg, #B662A6 -20%, #5B245C 70%)" }}
            >
              Calculate Engagement Impact
            </button>
          </div>

          {/* RESULTS PANEL */}
          <div className="rounded-[18px] border border-white/18 bg-white/7 p-[22px_18px]">
            <div className="text-[11px] mb-4 text-center font-extrabold tracking-[0.08em] text-white/60 uppercase">Results</div>

            <div className="mb-3.5 grid grid-cols-2 gap-1.75">
              <CalculatorStatCard label="Currently Unengaged"       value={formatCount(results.unengaged)}           subtext="members not reached"          />
              <CalculatorStatCard label="Incremental Members"       value={formatCount(results.incrementalMembers)}  subtext="newly reached with Bravo"     />
              <CalculatorStatCard label="New Product Relationships" value={formatCount(results.newProducts)}         subtext="estimated new products"       />
              <CalculatorStatCard label="Potential Deposit Growth"  value={formatCompactDollar(results.depositGrowth)} subtext="from engaged members" highlight />
            </div>

            <hr className="my-3.25 border-t border-none border-white/12" />

            <div className="mb-3.5 grid grid-cols-2 gap-1.75">
              <div className={`${styles.statCard} ${styles.statCardHighlight} ${styles.statCardCentered}`}>
                <div className={styles.statCardLabel}>Loan Origination Increase</div>
                <div className={`${styles.statCardValue} ${styles.statCardValueLarge}`}>{formatCompactDollar(results.loanOrigination)}</div>
                <div className={styles.statCardSubtext}>potential new loan volume</div>
              </div>
              <div className={`${styles.statCard} ${styles.statCardHighlight} ${styles.statCardCentered}`}>
                <div className={styles.statCardLabel}>Est. Call Deflection</div>
                <div className={`${styles.statCardValue} ${styles.statCardValueLarge}`}>{formatCount(results.callDeflection)}</div>
                <div className={styles.statCardSubtext}>inquiries handled by Bravo / yr</div>
              </div>
            </div>

            <hr className="my-3.25 border-t border-none border-white/12" />

            <div className="text-[10px] mb-1.5 pt-4 text-center font-bold tracking-[0.07em] text-white/50 uppercase">3-Year Engagement Projection</div>
            <div className="text-[9px] mb-7 text-center text-white/35">Total members engaged with Bravo</div>

            <CalculatorBarChart data={results.projection} formatValue={formatCount} maxBarHeight={60} chartHeight={80} />

            <p className="text-[11px] mt-2.5 text-center leading-normal text-white/40 italic">
              For illustration purposes only. Projections are based on the inputs provided and do not constitute a guarantee of results. Actual outcomes will vary.
            </p>
          </div>
        </div>
      </div>

      <CalculatorFooter
        sources={[
          "CFPB Financial Wellness Survey — fewer than 10% of members engage with traditional financial wellness programs.",
          "Filene Research Institute — personalized guidance drives 3.2× higher engagement than generic content.",
          "NCUA — average credit union member holds 2.4 product relationships; engaged members average 3.5+.",
        ]}
      />
    </div>
  );
};
